/**
 * VideoUtilsHelper - Utility class for video manipulation and processing
 *
 * Provides reusable video-related functions including:
 * - Thumbnail generation from video frames
 * - Video metadata extraction
 * - Frame extraction and processing
 * - Time/frame conversion utilities
 * - Video format detection and validation
 *
 * @class VideoUtilsHelper
 */

import RSVP from 'rsvp';

const MAX_REMOTE_RANGE_READ_BYTES = 4 * 1024 * 1024;
const MAX_REMOTE_TOTAL_READ_BYTES = 12 * 1024 * 1024;
const MAX_REMOTE_SOURCE_CACHE_BYTES = 8 * 1024 * 1024;
const FIRST_FRAME_THUMBNAIL_WIDTH = 640;
const FIRST_FRAME_THUMBNAIL_JPEG_QUALITY = 0.92;
const AUDIO_TRACK_PROBE_TIMEOUT_MS = 5000;

class VideoUtilsHelper {
	constructor() {
		this._canvas = null;
		this._context = null;
		this._mediabunny = null;
		this._mediabunnyLoadPromise = null;
	}

	/**
	 * Get singleton instance of VideoUtilsHelper
	 * @returns {VideoUtilsHelper}
	 */
	static getInstance() {
		if (!VideoUtilsHelper._instance) {
			VideoUtilsHelper._instance = new VideoUtilsHelper();
		}
		return VideoUtilsHelper._instance;
	}

	/**
	 * Lazy-load Mediabunny library for hardware-accelerated frame extraction
	 * @private
	 * @returns {Promise<Object>} Promise resolving to Mediabunny module
	 */
	async _loadMediabunny() {
		if (this._mediabunny) {
			return this._mediabunny;
		}

		if (this._mediabunnyLoadPromise) {
			return this._mediabunnyLoadPromise;
		}

		this._mediabunnyLoadPromise = import('mediabunny').then((module) => {
			this._mediabunny = module;
			return this._mediabunny;
		}).catch((error) => {
			console.warn('Failed to load Mediabunny (will use fallback):', error);
			this._mediabunnyLoadPromise = null;
			return null;
		});

		return this._mediabunnyLoadPromise;
	}

	/**
	 * Check if Mediabunny and WebCodecs API are supported
	 * @private
	 * @returns {boolean} True if Mediabunny can be used
	 */
	_isMediabunnySupported() {
		// Check for WebCodecs API support
		return ('VideoDecoder' in window && 'VideoEncoder' in window);
	}

	_parseContentLength(value) {
		const length = Number(value);
		return Number.isFinite(length) && length > 0 ? length : null;
	}

	_parseContentRangeSize(value) {
		if (!value) return null;
		const match = String(value).match(/\/(\d+)$/);
		if (!match) return null;
		return this._parseContentLength(match[1]);
	}

	_isRemoteHttpUrl(videoUrl) {
		return typeof videoUrl === 'string' && /^https?:\/\//i.test(videoUrl);
	}

	_cancelResponseBody(response) {
		try {
			if (response && response.body && response.body.cancel) {
				response.body.cancel();
			}
		} catch (_) {
			/* Nothing to clean up. */
		}
	}

	async _readBoundedResponseBody(response, maxBytes) {
		if (!response.body || !response.body.getReader) {
			throw new Error('Remote video response body is not stream-readable');
		}

		const reader = response.body.getReader();
		const chunks = [];
		let bytesRead = 0;
		try {
			while (true) {
				const { done, value } = await reader.read();
				if (done) break;
				if (!value || value.byteLength === 0) continue;
				bytesRead += value.byteLength;
				if (bytesRead > maxBytes) {
					await reader.cancel().catch(() => {});
					throw new Error('Remote video range body is larger than requested');
				}
				chunks.push(value);
			}
		} finally {
			reader.releaseLock();
		}

		const result = new Uint8Array(bytesRead);
		let offset = 0;
		chunks.forEach((chunk) => {
			result.set(chunk, offset);
			offset += chunk.byteLength;
		});
		return result;
	}

	_createBoundedRemoteSource(videoUrl, StreamSource) {
		const controllers = new Set();
		let totalRequestedBytes = 0;
		let disposed = false;
		const trackFetch = async (url, options) => {
			if (disposed) throw new Error('Remote video source disposed');
			const controller = new AbortController();
			controllers.add(controller);
			// Keep the signal until disposal: fetch can finish receiving headers
			// while its response body is still being read.
			return fetch(url, { ...options, signal: controller.signal });
		};

		const getSize = async () => {
			try {
				const response = await trackFetch(videoUrl, { method: 'HEAD', mode: 'cors' });
				const size = this._parseContentLength(response.headers.get('Content-Length'));
				this._cancelResponseBody(response);
				if (response.ok && size) return size;
			} catch (_) {
				/* Fall back to a one-byte range probe below. */
			}

			const response = await trackFetch(videoUrl, {
				method: 'GET',
				mode: 'cors',
				headers: { Range: 'bytes=0-0' },
			});
			const size = response.status === 206
				? this._parseContentRangeSize(response.headers.get('Content-Range'))
				: null;
			this._cancelResponseBody(response);
			if (response.status !== 206 || !size) {
				throw new Error('Unable to determine remote video size without downloading it');
			}
			return size;
		};

		const read = async (start, end) => {
			const requestedBytes = end - start;
			if (requestedBytes <= 0) return new Uint8Array();
			if (requestedBytes > MAX_REMOTE_RANGE_READ_BYTES) {
				throw new Error(`Refusing remote video range read over ${MAX_REMOTE_RANGE_READ_BYTES} bytes`);
			}
			if (totalRequestedBytes + requestedBytes > MAX_REMOTE_TOTAL_READ_BYTES) {
				throw new Error(`Refusing remote video reads over ${MAX_REMOTE_TOTAL_READ_BYTES} bytes`);
			}
			totalRequestedBytes += requestedBytes;

			const response = await trackFetch(videoUrl, {
				method: 'GET',
				mode: 'cors',
				headers: { Range: `bytes=${start}-${end - 1}` },
			});
			if (response.status !== 206) {
				this._cancelResponseBody(response);
				throw new Error('Remote video server did not honor bounded range request');
			}
			const contentLength = this._parseContentLength(response.headers.get('Content-Length'));
			if (contentLength && contentLength > requestedBytes) {
				this._cancelResponseBody(response);
				throw new Error('Remote video range response is larger than requested');
			}

			return this._readBoundedResponseBody(response, requestedBytes);
		};

		return new StreamSource({
			getSize,
			read,
			prefetchProfile: 'none',
			maxCacheSize: MAX_REMOTE_SOURCE_CACHE_BYTES,
			dispose: () => {
				disposed = true;
				controllers.forEach((controller) => controller.abort());
				controllers.clear();
			},
		});
	}

	/**
	 * Inspect container metadata without decoding audio or video.
	 * @param {string|Blob} mediaSource HTTP URL or local media file
	 * @returns {Promise<boolean|null>} null means the check was inconclusive.
	 */
	async hasAudioTrack(mediaSource) {
		let input = null;
		let timeoutId = null;
		let timedOut = false;
		const inspection = (async () => {
			const mediabunny = await this._loadMediabunny();
			if (!mediabunny || timedOut) return null;
			const { Input, BlobSource, StreamSource, ALL_FORMATS } = mediabunny;
			const source = typeof mediaSource === 'string'
				? this._createBoundedRemoteSource(mediaSource, StreamSource)
				: new BlobSource(mediaSource);
			input = new Input({ source, formats: ALL_FORMATS });
			if ((await input.getAudioTracks()).length > 0) return true;
			return (await input.getVideoTracks()).length > 0 ? false : null;
		})();
		try {
			return await Promise.race([
				inspection,
				new Promise((resolve) => {
					timeoutId = setTimeout(() => {
						timedOut = true;
						resolve(null);
					}, AUDIO_TRACK_PROBE_TIMEOUT_MS);
				}),
			]);
		} catch (_) {
			// Unknown formats, unavailable files and network failures must not
			// reject media that the existing transcription flow can still handle.
			return null;
		} finally {
			clearTimeout(timeoutId);
			if (input) input.dispose();
		}
	}

	async _getDurationWithMediabunny(videoSource) {
		const mediabunny = await this._loadMediabunny();
		if (!mediabunny) {
			throw new Error('Mediabunny not available');
		}

		const { Input, BlobSource, StreamSource, ALL_FORMATS } = mediabunny;
		const source = typeof videoSource === 'string'
			? this._createBoundedRemoteSource(videoSource, StreamSource)
			: new BlobSource(videoSource);
		const input = new Input({ source, formats: ALL_FORMATS });
		try {
			return await input.computeDuration();
		} finally {
			if (input.dispose) input.dispose();
		}
	}


	/**
	 * Extract frames using Mediabunny (hardware-accelerated, fast)
	 * @private
	 * @param {string|Blob} videoSource - Video URL string (uses bounded HTTP range reads) or Blob (uses BlobSource)
	 * @param {Array<number>} timestamps - Array of timestamps in seconds
	 * @param {number} width - Frame width
	 * @param {number} height - Frame height
	 * @param {Function} progressCallback - Optional progress callback
	 * @returns {Promise<Array<HTMLCanvasElement>>} Array of extracted frame canvases
	 */
	async _extractFramesWithMediabunny(videoSource, timestamps, width, height, progressCallback = null) {
		const mediabunny = await this._loadMediabunny();
		if (!mediabunny) {
			throw new Error('Mediabunny not available');
		}

		// Mediabunny requires positive integer dimensions (cannot be 0 or fractional)
		// Coerce to integers and enforce minimum of 1px to prevent errors
		const intWidth = Math.max(1, Math.floor(width));
		const intHeight = Math.max(1, Math.floor(height));

		// Import Mediabunny classes
		const { Input, BlobSource, StreamSource, CanvasSink, ALL_FORMATS } = mediabunny;

		// Use a bounded StreamSource for URL strings. Mediabunny's UrlSource
		// can issue `Range: bytes=0-`, which some S3/CloudFront URLs answer
		// with the whole object.
		// Use BlobSource for Blob objects (backward-compatible for callers passing a Blob)
		const source = typeof videoSource === 'string'
			? this._createBoundedRemoteSource(videoSource, StreamSource)
			: new BlobSource(videoSource);

		// Create Input from video source
		const input = new Input({ source, formats: ALL_FORMATS });
		try {
			// Get primary video track
			const videoTrack = await input.getPrimaryVideoTrack();

			if (!videoTrack) {
				throw new Error('Video has no video track');
			}
			if (videoTrack.codec === null) {
				throw new Error('Unsupported video codec');
			}
			if (!(await videoTrack.canDecode())) {
				throw new Error('Unable to decode the video track');
			}

			// Create CanvasSink with desired dimensions (must be positive integers)
			const sink = new CanvasSink(videoTrack, {
				width: intWidth,
				height: intHeight,
				fit: 'fill', // Fill the canvas (like object-fit: cover)
			});

			// Extract frames at specified timestamps using optimized pipeline
			// Process frames in parallel as they arrive for maximum performance
			const framePromises = [];
			let completedCount = 0;

			for await (const wrappedCanvas of sink.canvasesAtTimestamps(timestamps)) {
				if (wrappedCanvas) {
					// wrappedCanvas.canvas is HTMLCanvasElement or OffscreenCanvas
					// Store canvas directly (already at correct dimensions)
					framePromises.push(Promise.resolve(wrappedCanvas.canvas));
				}

				// Report progress
				completedCount++;
				if (progressCallback) {
					const percent = (completedCount / timestamps.length) * 100;
					progressCallback(percent);
				}
			}

			// Wait for all frames to be ready (should be immediate since they're already resolved)
			const frames = await Promise.all(framePromises);
			return frames;
		} finally {
			if (input.dispose) input.dispose();
		}
		}

	/**
	 * Extract a single thumbnail from a video at a specific time
	 *
	 * @param {string} videoUrl - URL of the video file
	 * @param {number} timeInSeconds - Time position to extract thumbnail
	 * @param {number} width - Desired thumbnail width (optional, maintains aspect ratio)
	 * @param {number} height - Desired thumbnail height (optional, maintains aspect ratio)
	 * @returns {Promise<string>} Promise resolving to base64 data URL of thumbnail
	 *
	 * @example
	 * const thumbnail = await VideoUtilsHelper.getInstance().extractThumbnail(
	 *   'video.mp4',
	 *   5.0,  // 5 seconds into video
	 *   320,  // width
	 *   180   // height
	 * );
	 */
	extractThumbnail(videoUrl, timeInSeconds, width = null, height = null) {
		return new RSVP.Promise((resolve, reject) => {
			const video = document.createElement('video');
			video.crossOrigin = 'anonymous';
			video.preload = 'metadata';

			// Store calculated dimensions to preserve aspect ratio
			let targetWidth;
			let targetHeight;

			video.addEventListener('loadedmetadata', () => {
				// Calculate dimensions maintaining aspect ratio
				const videoWidth = video.videoWidth;
				const videoHeight = video.videoHeight;
				const aspectRatio = videoWidth / videoHeight;

				targetWidth = width || videoWidth;
				targetHeight = height || videoHeight;

				if (width && !height) {
					targetHeight = targetWidth / aspectRatio;
				} else if (height && !width) {
					targetWidth = targetHeight * aspectRatio;
				}

				// Seek to desired time
				video.currentTime = Math.min(timeInSeconds, video.duration);
			});

			video.addEventListener('seeked', () => {
				try {
					const canvas = document.createElement('canvas');
					canvas.width = targetWidth;
					canvas.height = targetHeight;

					const ctx = canvas.getContext('2d');
					ctx.drawImage(video, 0, 0, targetWidth, targetHeight);

					const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.85);

					// Cleanup
					video.src = '';
					video.load();

					resolve(thumbnailDataUrl);
				} catch (error) {
					reject(new Error(`Failed to extract thumbnail: ${error.message}`));
				}
			});

			video.addEventListener('error', (e) => {
				reject(new Error(`Video loading failed: ${e.message || 'Unknown error'}`));
			});

			video.src = videoUrl;
		});
	}

	/**
	 * Extract multiple thumbnails from a video at evenly spaced intervals
	 *
	 * Uses Mediabunny (hardware-accelerated) when available, falls back to HTML5 video.
	 *
	 * @param {string} videoUrl - URL of the video file
	 * @param {number} count - Number of thumbnails to extract
	 * @param {number} width - Thumbnail width
	 * @param {number} height - Thumbnail height
	 * @param {function} progressCallback - Optional callback for progress updates (0-100)
	 * @returns {Promise<Array<string>>} Promise resolving to array of base64 data URLs
	 *
	 * @example
	 * const thumbnails = await VideoUtilsHelper.getInstance().extractThumbnails(
	 *   'video.mp4',
	 *   10,  // 10 thumbnails
	 *   160, // width
	 *   90,  // height
	 *   (progress) => console.log(`Progress: ${progress}%`)
	 * );
	 */
	async extractThumbnails(videoUrl, count, width, height, progressCallback = null) {
		const isRemoteHttpUrl = this._isRemoteHttpUrl(videoUrl);
		// Try Mediabunny first (fast, hardware-accelerated)
		if (this._isMediabunnySupported()) {
			try {
				const startTime = performance.now();

				// Calculate duration without handing remote URLs to an HTML video
				// element, which can make browser-controlled range requests.
				const duration = isRemoteHttpUrl
					? await this._getDurationWithMediabunny(videoUrl)
					: (await this.getVideoMetadata(videoUrl)).duration;
				if (!Number.isFinite(duration) || duration <= 0) {
					throw new Error('Unable to determine video duration');
				}

				// Calculate timestamps for evenly distributed frames
				const timestamps = [];
				const interval = duration / (count + 1); // +1 to avoid first/last frames
				for (let i = 0; i < count; i++) {
					timestamps.push(interval * (i + 1));
				}

				// Extract frames with Mediabunny through bounded HTTP range reads.
				const extractStartTime = performance.now();
				const frameCanvases = await this._extractFramesWithMediabunny(
					videoUrl,
					timestamps,
					width,
					height,
					progressCallback
				);
				const extractTime = performance.now() - extractStartTime;
				console.log(`Frame extraction time: ${extractTime.toFixed(0)}ms`);

				// Convert canvases to data URLs in parallel
				const convertStartTime = performance.now();
				const thumbnails = frameCanvases.map(canvas => {
					return canvas.toDataURL('image/jpeg', 0.85);
				});
				const convertTime = performance.now() - convertStartTime;
				console.log(`Canvas to data URL conversion time: ${convertTime.toFixed(0)}ms`);

				const totalTime = performance.now() - startTime;
				console.log(`✅ Extracted ${thumbnails.length} thumbnails using Mediabunny in ${totalTime.toFixed(0)}ms (${(totalTime/1000).toFixed(2)}s)`);
				return thumbnails;

			} catch (error) {
				console.warn('Mediabunny extraction failed:', error);
				if (isRemoteHttpUrl) {
					throw new Error('Refusing HTML5 video fallback for remote video URL');
				}
				// Fall through to HTML5 fallback below
			}
		}

		if (isRemoteHttpUrl) {
			throw new Error('Mediabunny unavailable; refusing HTML5 video fallback for remote video URL');
		}

		// Fallback to HTML5 video (slower but reliable)
		console.log('Using HTML5 video fallback for thumbnail extraction');
		const fallbackStartTime = performance.now();
		const result = await this._extractThumbnailsWithHTML5Video(videoUrl, count, width, height, progressCallback);
		const fallbackTime = performance.now() - fallbackStartTime;
		console.log(`HTML5 video extraction completed in ${fallbackTime.toFixed(0)}ms (${(fallbackTime/1000).toFixed(2)}s)`);
		return result;
	}

	/**
	 * Extract the first decodable frame from a video URL as a JPEG Blob.
	 *
	 * Uses a bounded Mediabunny StreamSource so the full video file is never
	 * downloaded. Returns null if mediabunny is unavailable, the video track
	 * cannot be decoded, the server ignores byte ranges, or any error occurs.
	 *
	 * @param {string} videoUrl - URL of the video file
	 * @param {number} width - Desired output width in pixels (default 640)
	 * @returns {Promise<Blob|null>} Promise resolving to a JPEG Blob, or null on failure
	 */
	async extractFirstFrameAsBlob(videoUrl, width = FIRST_FRAME_THUMBNAIL_WIDTH) {
		const mediabunny = await this._loadMediabunny();
		if (!mediabunny) return null;

		const { Input, StreamSource, CanvasSink, ALL_FORMATS } = mediabunny;
		let input = null;
		try {
			input = new Input({
				source: this._createBoundedRemoteSource(videoUrl, StreamSource),
				formats: ALL_FORMATS,
			});
			const videoTrack = await input.getPrimaryVideoTrack();
			if (!videoTrack || videoTrack.codec === null) return null;
			if (!(await videoTrack.canDecode())) return null;

			const intWidth = Math.max(1, Math.floor(width));
			// Omit height so mediabunny derives it from the source aspect ratio.
			// Passing height: 1 produced a 1px-tall JPEG that the card then stretched.
			const sink = new CanvasSink(videoTrack, { width: intWidth, fit: 'contain' });
			const rawFirstTimestamp = await videoTrack.getFirstTimestamp();
			const firstTimestamp = Number.isFinite(rawFirstTimestamp) ? Math.max(0, rawFirstTimestamp) : 0;

			for await (const wrappedCanvas of sink.canvases(firstTimestamp)) {
				return await new Promise((resolve) => {
					const canvas = wrappedCanvas.canvas;
					if (canvas.convertToBlob) {
						canvas.convertToBlob({ type: 'image/jpeg', quality: FIRST_FRAME_THUMBNAIL_JPEG_QUALITY }).then(resolve).catch(() => resolve(null));
					} else {
						canvas.toBlob(resolve, 'image/jpeg', FIRST_FRAME_THUMBNAIL_JPEG_QUALITY);
					}
				});
			}
			return null;
		} catch (err) {
			console.warn(JSON.stringify({
				event: 'extractFirstFrameAsBlob_failed',
				error: err && err.message
			}));
			return null;
		} finally {
			if (input && input.dispose) input.dispose();
		}
	}

	/**
	 * Extract thumbnails using HTML5 video element (fallback method)
	 * @private
	 * @param {string} videoUrl - URL of the video file
	 * @param {number} count - Number of thumbnails to extract
	 * @param {number} width - Thumbnail width
	 * @param {number} height - Thumbnail height
	 * @param {function} progressCallback - Optional callback for progress updates (0-100)
	 * @returns {Promise<Array<string>>} Promise resolving to array of base64 data URLs
	 */
	_extractThumbnailsWithHTML5Video(videoUrl, count, width, height, progressCallback = null) {
		return new RSVP.Promise((resolve, reject) => {
			const video = document.createElement('video');
			video.crossOrigin = 'anonymous';
			video.preload = 'metadata';

			const thumbnails = [];
			let currentIndex = 0;

			video.addEventListener('loadedmetadata', () => {
				const duration = video.duration;
				const interval = duration / (count + 1); // +1 to avoid first/last frames

				const extractNext = () => {
					if (currentIndex >= count) {
						// Cleanup
						video.src = '';
						video.load();
						resolve(thumbnails);
						return;
					}

					const timePosition = interval * (currentIndex + 1);
					video.currentTime = timePosition;
				};

				extractNext();
			});

			video.addEventListener('seeked', () => {
				try {
					const canvas = document.createElement('canvas');
					canvas.width = width;
					canvas.height = height;

					const ctx = canvas.getContext('2d');
					ctx.drawImage(video, 0, 0, width, height);

					thumbnails.push(canvas.toDataURL('image/jpeg', 0.85));
					currentIndex++;

					// Update progress
					if (progressCallback) {
						const progress = Math.round((currentIndex / count) * 100);
						progressCallback(progress);
					}

					// Extract next thumbnail
					if (currentIndex < count) {
						const duration = video.duration;
						const interval = duration / (count + 1);
						const timePosition = interval * (currentIndex + 1);
						video.currentTime = timePosition;
					} else {
						// Done
						video.src = '';
						video.load();
						resolve(thumbnails);
					}
				} catch (error) {
					video.src = '';
					video.load();
					reject(new Error(`Failed to extract thumbnail ${currentIndex}: ${error.message}`));
				}
			});

			video.addEventListener('error', (e) => {
				reject(new Error(`Video loading failed: ${e.message || 'Unknown error'}`));
			});

			video.src = videoUrl;
		});
	}

	/**
	 * Get video metadata (duration, dimensions, fps if available)
	 *
	 * @param {string} videoUrl - URL of the video file
	 * @returns {Promise<Object>} Promise resolving to metadata object
	 *
	 * @example
	 * const metadata = await VideoUtilsHelper.getInstance().getVideoMetadata('video.mp4');
	 * console.log(metadata);
	 * // {
	 * //   duration: 60.5,
	 * //   width: 1920,
	 * //   height: 1080,
	 * //   aspectRatio: 1.777,
	 * //   fps: 30 (if detectable)
	 * // }
	 */
	getVideoMetadata(videoUrl) {
		return new RSVP.Promise((resolve, reject) => {
			const video = document.createElement('video');
			video.crossOrigin = 'anonymous';
			video.preload = 'metadata';

			video.addEventListener('loadedmetadata', async () => {
				const metadata = {
					duration: video.duration,
					width: video.videoWidth,
					height: video.videoHeight,
					aspectRatio: video.videoWidth / video.videoHeight
				};

				// Try to detect FPS (browser support varies)
				try {
					const fps = await this._detectVideoFPS(video);
					if (fps) {
						metadata.fps = fps;
					}
				} catch (e) {
					console.warn('Could not detect video FPS:', e);
				}

				// Cleanup
				video.src = '';
				video.load();

				resolve(metadata);
			});

			video.addEventListener('error', (e) => {
				reject(new Error(`Failed to load video metadata: ${e.message || 'Unknown error'}`));
			});

			video.src = videoUrl;
		});
	}

	/**
	 * Detect video frame rate using MediaStream API
	 *
	 * Note: Browser support varies significantly:
	 * - Chrome/Edge: Good support via MediaStream Track Settings
	 * - Firefox: Limited support, may not expose frameRate
	 * - Safari: Minimal support
	 *
	 * @private
	 * @param {HTMLVideoElement} videoElement - Video element to analyze
	 * @returns {Promise<number|null>} Detected FPS or null if detection fails
	 */
	async _detectVideoFPS(videoElement) {
		if (!videoElement.captureStream) {
			return null;
		}

		let stream;
		try {
			stream = videoElement.captureStream();
			const videoTrack = stream.getVideoTracks()[0];

			if (videoTrack && videoTrack.getSettings) {
				const settings = videoTrack.getSettings();
				if (settings.frameRate) {
					const frameRate = settings.frameRate;
					stream.getTracks().forEach(track => track.stop());
					return frameRate;
				}
			}
		} catch (e) {
			console.warn('FPS detection failed:', e);
		} finally {
			// Always stop stream tracks to free GPU/decoder resources
			if (stream) {
				stream.getTracks().forEach(track => track.stop());
			}
		}

		return null;
	}

	/**
	 * Convert time in seconds to frame number
	 *
	 * @param {number} timeInSeconds - Time in seconds
	 * @param {number} fps - Frames per second
	 * @returns {number} Frame number (0-indexed)
	 *
	 * @example
	 * const frame = VideoUtilsHelper.getInstance().timeToFrame(2.5, 30);
	 * // Returns: 75 (2.5 seconds * 30 fps)
	 */
	timeToFrame(timeInSeconds, fps) {
		return Math.floor(timeInSeconds * fps);
	}

	/**
	 * Convert frame number to time in seconds
	 *
	 * @param {number} frameNumber - Frame number (0-indexed)
	 * @param {number} fps - Frames per second
	 * @returns {number} Time in seconds
	 *
	 * @example
	 * const time = VideoUtilsHelper.getInstance().frameToTime(75, 30);
	 * // Returns: 2.5 (75 frames / 30 fps)
	 */
	frameToTime(frameNumber, fps) {
		return frameNumber / fps;
	}

	/**
	 * Format time in seconds to human-readable string
	 *
	 * @param {number} timeInSeconds - Time in seconds
	 * @param {boolean} showMilliseconds - Whether to include milliseconds
	 * @returns {string} Formatted time string (HH:MM:SS or HH:MM:SS.mmm)
	 *
	 * @example
	 * VideoUtilsHelper.getInstance().formatTime(125.456, true);
	 * // Returns: "00:02:05.456"
	 *
	 * VideoUtilsHelper.getInstance().formatTime(125.456, false);
	 * // Returns: "00:02:05"
	 */
	formatTime(timeInSeconds, showMilliseconds = false) {
		const hours = Math.floor(timeInSeconds / 3600);
		const minutes = Math.floor((timeInSeconds % 3600) / 60);
		const seconds = Math.floor(timeInSeconds % 60);
		const milliseconds = Math.floor((timeInSeconds % 1) * 1000);

		const pad = (num, size = 2) => String(num).padStart(size, '0');

		let formatted = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

		if (showMilliseconds) {
			formatted += `.${pad(milliseconds, 3)}`;
		}

		return formatted;
	}

	/**
	 * Validate video URL and check if format is supported
	 *
	 * @param {string} videoUrl - URL to validate
	 * @returns {Object} Validation result with format info
	 *
	 * @example
	 * const result = VideoUtilsHelper.getInstance().validateVideoUrl('video.mp4');
	 * // Returns: { isValid: true, format: 'mp4', mimeType: 'video/mp4' }
	 */
	validateVideoUrl(videoUrl) {
		if (!videoUrl || typeof videoUrl !== 'string') {
			return { isValid: false, error: 'Invalid URL' };
		}

		const supportedFormats = {
			'mp4': 'video/mp4',
			'webm': 'video/webm',
			'ogg': 'video/ogg',
			'ogv': 'video/ogg',
			'mov': 'video/quicktime',
			'm4v': 'video/mp4'
		};

		const extension = videoUrl.split('.').pop().toLowerCase().split('?')[0];

		if (supportedFormats[extension]) {
			return {
				isValid: true,
				format: extension,
				mimeType: supportedFormats[extension]
			};
		}

		return {
			isValid: false,
			error: `Unsupported format: ${extension}`,
			supportedFormats: Object.keys(supportedFormats)
		};
	}

	/**
	 * Check if browser can play a specific video format
	 *
	 * @param {string} mimeType - MIME type to check (e.g., 'video/mp4; codecs="avc1.42E01E"')
	 * @returns {boolean} True if format is supported
	 *
	 * @example
	 * const canPlayMP4 = VideoUtilsHelper.getInstance().canPlayFormat('video/mp4');
	 * const canPlayWebM = VideoUtilsHelper.getInstance().canPlayFormat('video/webm; codecs="vp9"');
	 */
	canPlayFormat(mimeType) {
		const video = document.createElement('video');
		const canPlay = video.canPlayType(mimeType);
		return canPlay === 'probably' || canPlay === 'maybe';
	}

	/**
	 * Calculate optimal thumbnail dimensions maintaining aspect ratio
	 *
	 * @param {number} videoWidth - Original video width
	 * @param {number} videoHeight - Original video height
	 * @param {number} maxWidth - Maximum thumbnail width
	 * @param {number} maxHeight - Maximum thumbnail height
	 * @returns {Object} Optimal dimensions { width, height }
	 *
	 * @example
	 * const dims = VideoUtilsHelper.getInstance().calculateThumbnailDimensions(
	 *   1920, 1080,  // 1080p video
	 *   320, 240     // max thumbnail size
	 * );
	 * // Returns: { width: 320, height: 180 }
	 */
	calculateThumbnailDimensions(videoWidth, videoHeight, maxWidth, maxHeight) {
		const videoAspectRatio = videoWidth / videoHeight;
		const maxAspectRatio = maxWidth / maxHeight;

		let width, height;

		if (videoAspectRatio > maxAspectRatio) {
			// Video is wider than max dimensions
			width = maxWidth;
			height = Math.round(maxWidth / videoAspectRatio);
		} else {
			// Video is taller than max dimensions
			height = maxHeight;
			width = Math.round(maxHeight * videoAspectRatio);
		}

		return { width, height };
	}

	/**
	 * Create a thumbnail strip (filmstrip) from a video
	 *
	 * @param {string} videoUrl - URL of the video file
	 * @param {number} thumbnailCount - Number of thumbnails in strip
	 * @param {number} thumbnailWidth - Width of each thumbnail
	 * @param {number} thumbnailHeight - Height of each thumbnail
	 * @returns {Promise<string>} Promise resolving to base64 data URL of filmstrip
	 *
	 * @example
	 * const filmstrip = await VideoUtilsHelper.getInstance().createThumbnailStrip(
	 *   'video.mp4',
	 *   10,  // 10 frames
	 *   160, // each 160px wide
	 *   90   // each 90px tall
	 * );
	 * // Returns single image with all thumbnails side-by-side
	 */
	createThumbnailStrip(videoUrl, thumbnailCount, thumbnailWidth, thumbnailHeight) {
		return new RSVP.Promise(async (resolve, reject) => {
			try {
				const thumbnails = await this.extractThumbnails(
					videoUrl,
					thumbnailCount,
					thumbnailWidth,
					thumbnailHeight
				);

				// Create a single canvas with all thumbnails
				const canvas = document.createElement('canvas');
				canvas.width = thumbnailWidth * thumbnailCount;
				canvas.height = thumbnailHeight;

				const ctx = canvas.getContext('2d');

				// Draw each thumbnail onto the strip
				const promises = thumbnails.map((thumbDataUrl, index) => {
					return new RSVP.Promise((resolveImg) => {
						const img = new Image();
						img.onload = () => {
							ctx.drawImage(
								img,
								index * thumbnailWidth,
								0,
								thumbnailWidth,
								thumbnailHeight
							);
							resolveImg();
						};
						img.src = thumbDataUrl;
					});
				});

				await RSVP.Promise.all(promises);

				const filmstripDataUrl = canvas.toDataURL('image/jpeg', 0.85);
				resolve(filmstripDataUrl);

			} catch (error) {
				reject(new Error(`Failed to create thumbnail strip: ${error.message}`));
			}
		});
	}

	/**
	 * Get canvas for drawing operations (lazily initialized singleton pattern)
	 *
	 * This method implements a singleton pattern for canvas resources to avoid
	 * repeated DOM element creation. The canvas is created once and reused
	 * across multiple operations for better performance.
	 *
	 * @private
	 * @returns {{canvas: HTMLCanvasElement, context: CanvasRenderingContext2D}} Canvas and 2D context
	 */
	_getCanvas() {
		if (!this._canvas) {
			this._canvas = document.createElement('canvas');
			this._context = this._canvas.getContext('2d');
		}
		return { canvas: this._canvas, context: this._context };
	}

	/**
	 * Clean up resources
	 */
	destroy() {
		this._canvas = null;
		this._context = null;
	}
}

// Export singleton instance
export default VideoUtilsHelper;
